import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, CalendarDays } from 'lucide-react'
import { ManagementCompanyNavigation } from '@/components'
import { Card } from '@/components/ui'
import { PeriodSelector } from '@/components/analytics'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuthStore, useStudioStore } from '@/store'
import { getSupabase } from '@/lib/supabase'
import { withAuthRecovery } from '@/lib/authRecovery'
import { getLocalIsoDate } from '@/lib/analytics/periodCalculations'
import { formatNumber } from '@/lib/utils'
import type { MetricPeriod } from '@/lib/utils'
import { listPartnerCoverage } from '@/services/territorialManagerService'
import type { PartnerCoverageRow } from '@/types'

type StudioSubscriptionType = 'REGULAR' | 'SUBSCRIPTION' | 'MIXED'
type PeriodRangeKey = 'period' | `week-${number}`

interface MetricWeek {
  week_id: number
  period_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
}

interface SubscriptionSettingsRow {
  studio_internal_id: number
  subscription_type: StudioSubscriptionType
  first_subscription_sale_date: string | null
}

interface DailyTotalsRow {
  studio_internal_id: number
  metric_date: string
  total_clients_count: number | null
  new_visitors_count: number | null
  new_sales_count: number | null
  new_subscriptions_count: number | null
  subscription_upgrades_count: number | null
  subscription_returning_clients_count: number | null
  active_subscribers_count: number | null
  gross_revenue_rub: number | null
  new_sales_amount_rub: number | null
  membership_fees_new_amount_rub: number | null
  new_subscriptions_amount_rub: number | null
  subscription_upgrades_amount_rub: number | null
  subscription_returning_clients_amount_rub: number | null
  membership_fees_subscription_upgrades_amount_rub: number | null
  membership_fees_subscription_returning_amount_rub: number | null
}

interface ActiveSubscribersSnapshotRow {
  studio_internal_id: number
  metric_date: string
  active_subscribers_count: number | null
}

interface WeeklyPlanRow {
  studio_internal_id: number
  period_id: number
  partner_number: number | null
  week_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  weekly_gross_revenue_plan: number | null
  weekly_gross_revenue_fact: number | null
  weekly_new_subscriptions_count_plan: number | null
  weekly_new_subscriptions_count_fact: number | null
  weekly_subscription_upgrades_count_plan: number | null
  weekly_subscription_upgrades_count_fact: number | null
  weekly_subscription_returning_clients_count_plan: number | null
  weekly_subscription_returning_clients_count_fact: number | null
}

interface StudioMetaData {
  studio_number: string
  city_name: string
  country: string | null
  partner_number: number | null
  partner_label: string
  address: string
  city_land_group: number | null
  city_population_group: number | null
}

interface PartnerSubscriptionWeekValue {
  label: string
  plan: number
  fact: number
  plan_new?: number
  fact_new?: number
  fact_upgrades?: number
  fact_returning?: number
  week_end?: string
  active_subscribers?: number
  active_subscribers_growth?: number | null
  new_visitors?: number
  new_subscriptions?: number
  conversion_pct?: number | null
  churn_count?: number | null
  churn_pct?: number | null
  retention_pct?: number | null
}

interface StudioSubscriptionRow {
  studio_internal_id: number
  studio_number: string
  city_name: string
  address: string
  partner_number: number
  partner_label: string
  partner_short_label: string
  period_plan: number
  period_fact: number
  period_plan_new: number
  period_fact_new: number
  period_fact_upgrades: number
  period_fact_returning: number
  completion_pct: number
  active_subscribers: number
  active_subscribers_growth: number | null
  new_visitors: number
  new_subscriptions: number
  conversion_pct: number | null
  subscribers_at_start: number | null
  subscribers_at_end: number | null
  churn_count: number | null
  churn_pct: number | null
  retention_pct: number | null
  weeks: PartnerSubscriptionWeekValue[]
}

interface DailyTotalsQueryParams {
  studioIds: number[]
  startDate?: string
  endDate?: string
  exactDate?: string
}

interface SubscriptionLevel {
  threshold: number
  name: string
  color: string
}

const SUBSCRIPTION_LEVELS: SubscriptionLevel[] = [
  { threshold: 0, name: 'Старт', color: '#94a3b8' },
  { threshold: 1, name: 'Первая!', color: '#a78bfa' },
  { threshold: 3, name: 'Разгон', color: '#60a5fa' },
  { threshold: 5, name: 'Пятерка', color: '#38bdf8' },
  { threshold: 10, name: 'Десятка', color: '#f97316' },
  { threshold: 15, name: 'Прорыв', color: '#fb923c' },
  { threshold: 25, name: 'Серебро', color: '#cbd5e1' },
  { threshold: 50, name: 'Золото', color: '#fbbf24' },
  { threshold: 75, name: 'Платина', color: '#67e8f9' },
  { threshold: 100, name: 'Сотня', color: '#f59e0b' }
]

const MANAGEMENT_BRANCH_PARTNER_NUMBER = 1000

function addDaysToIsoDate(dateString: string, days: number): string {
  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(Date.UTC(year, (month || 1) - 1, day || 1))
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

function formatDisplayDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long'
  })
}

function formatDisplayRange(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  const monthNamesGenitive = [
    'января',
    'февраля',
    'марта',
    'апреля',
    'мая',
    'июня',
    'июля',
    'августа',
    'сентября',
    'октября',
    'ноября',
    'декабря'
  ]
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()
  const startMonthLabel = monthNamesGenitive[start.getMonth()] || ''
  const endMonthLabel = monthNamesGenitive[end.getMonth()] || ''

  if (sameMonth) {
    return `${start.getDate()}-${end.getDate()} ${endMonthLabel}`
  }

  return `${start.getDate()} ${startMonthLabel} - ${end.getDate()} ${endMonthLabel}`
}

function computeWeekFromPeriod(period: MetricPeriod, targetDate: string): MetricWeek | null {
  const periodStart = new Date(`${period.period_start}T00:00:00`)
  const target = new Date(`${targetDate}T00:00:00`)
  const daysSinceStart = Math.floor((target.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24))

  if (daysSinceStart < 0) {
    return null
  }

  const weekNumber = Math.floor(daysSinceStart / 7) + 1
  const weekStart = new Date(periodStart)
  weekStart.setDate(weekStart.getDate() + (weekNumber - 1) * 7)

  const weekEnd = new Date(weekStart)
  weekEnd.setDate(weekEnd.getDate() + 6)

  const periodEnd = new Date(`${period.period_end}T00:00:00`)
  if (weekEnd > periodEnd) {
    weekEnd.setTime(periodEnd.getTime())
  }

  return {
    week_id: -weekNumber,
    period_id: period.period_id,
    week_number_in_period: weekNumber,
    week_start: getLocalIsoDate(weekStart),
    week_end: getLocalIsoDate(weekEnd),
    week_len_days: Math.floor((weekEnd.getTime() - weekStart.getTime()) / (1000 * 60 * 60 * 24)) + 1
  }
}

// Special accent tone for the partner subscriptions table: always use yellow/amber
// for top rows and keep other rows without colored background.
function partnerRowAccentTone(place: number): string {
  if (place === 1) return 'border-l-4 border-l-amber-400 bg-amber-50/60'
  if (place === 2) return 'border-l-4 border-l-amber-400 bg-amber-50/60'
  if (place === 3) return 'border-l-4 border-l-amber-400 bg-amber-50/60'
  return 'border-l-4 border-l-transparent'
}

async function fetchDailyTotalsChunked(
  supabase: ReturnType<typeof getSupabase>,
  params: DailyTotalsQueryParams
): Promise<DailyTotalsRow[]> {
  const { studioIds, startDate, endDate, exactDate } = params
  if (studioIds.length === 0) return []

  const chunkSize = 150
  const pageSize = 1000
  const rows: DailyTotalsRow[] = []

  for (let index = 0; index < studioIds.length; index += chunkSize) {
    const chunk = studioIds.slice(index, index + chunkSize)
    let pageFrom = 0

    while (true) {
      let query = supabase
        .from('studio_daily_totals_with_rub')
        .select(`
          studio_internal_id,
          metric_date,
          total_clients_count,
          new_visitors_count,
          new_sales_count,
          new_subscriptions_count,
          subscription_upgrades_count,
          subscription_returning_clients_count,
          active_subscribers_count,
          gross_revenue_rub,
          new_sales_amount_rub,
          membership_fees_new_amount_rub,
          new_subscriptions_amount_rub,
          subscription_upgrades_amount_rub,
          subscription_returning_clients_amount_rub,
          membership_fees_subscription_upgrades_amount_rub,
          membership_fees_subscription_returning_amount_rub
        `)
        .in('studio_internal_id', chunk)
        .order('metric_date', { ascending: true })
        .order('studio_internal_id', { ascending: true })
        .range(pageFrom, pageFrom + pageSize - 1)

      if (exactDate) {
        query = query.eq('metric_date', exactDate)
      } else {
        if (startDate) query = query.gte('metric_date', startDate)
        if (endDate) query = query.lte('metric_date', endDate)
      }

      const { data, error } = await withAuthRecovery(() => query) as {
        data: DailyTotalsRow[] | null
        error: { message?: string } | null
      }

      if (error) {
        throw new Error(error.message || 'Не удалось загрузить факты по рейтингам')
      }

      const batch = data || []
      rows.push(...batch)
      if (batch.length < pageSize) break
      pageFrom += batch.length
    }
  }

  return rows
}

async function fetchActiveSubscriberSnapshotsChunked(
  supabase: ReturnType<typeof getSupabase>,
  studioIds: number[],
  startDate: string,
  endDate: string
): Promise<Map<number, ActiveSubscribersSnapshotRow[]>> {
  if (studioIds.length === 0) return new Map()

  const chunkSize = 150
  const pageSize = 1000
  const result = new Map<number, ActiveSubscribersSnapshotRow[]>()

  for (let index = 0; index < studioIds.length; index += chunkSize) {
    const chunk = studioIds.slice(index, index + chunkSize)
    let pageFrom = 0

    while (true) {
      const { data, error } = await withAuthRecovery(() =>
        supabase
          .from('studio_daily_totals_with_rub')
          .select('studio_internal_id, metric_date, active_subscribers_count')
          .in('studio_internal_id', chunk)
          .gte('metric_date', startDate)
          .lte('metric_date', endDate)
          .not('active_subscribers_count', 'is', null)
          .order('metric_date', { ascending: true })
          .order('studio_internal_id', { ascending: true })
          .range(pageFrom, pageFrom + pageSize - 1)
      ) as { data: ActiveSubscribersSnapshotRow[] | null; error: { message?: string } | null }

      if (error) {
        throw new Error(error.message || 'Не удалось загрузить текущие активные подписки')
      }

      const batch = data || []
      for (const row of batch) {
        const list = result.get(row.studio_internal_id) || []
        list.push(row)
        result.set(row.studio_internal_id, list)
      }

      if (batch.length < pageSize) break
      pageFrom += batch.length
    }
  }

  return result
}

async function fetchWeeklyPlansChunked(
  supabase: ReturnType<typeof getSupabase>,
  studioIds: number[],
  weekStart: string,
  weekEnd: string
): Promise<WeeklyPlanRow[]> {
  if (studioIds.length === 0) return []

  const chunkSize = 150
  const pageSize = 1000
  const rows: WeeklyPlanRow[] = []

  for (let index = 0; index < studioIds.length; index += chunkSize) {
    const chunk = studioIds.slice(index, index + chunkSize)
    let pageFrom = 0

    while (true) {
      const { data, error } = await withAuthRecovery(() =>
        supabase
          .from('studio_weekly_plan_vs_fact')
          .select(`
            studio_internal_id,
            period_id,
            partner_number,
            week_id,
            week_number_in_period,
            week_start,
            week_end,
            weekly_gross_revenue_plan,
            weekly_gross_revenue_fact,
            weekly_new_subscriptions_count_plan,
            weekly_new_subscriptions_count_fact,
            weekly_subscription_upgrades_count_plan,
            weekly_subscription_upgrades_count_fact,
            weekly_subscription_returning_clients_count_plan,
            weekly_subscription_returning_clients_count_fact
          `)
          .eq('week_start', weekStart)
          .eq('week_end', weekEnd)
          .in('studio_internal_id', chunk)
          .order('studio_internal_id', { ascending: true })
          .range(pageFrom, pageFrom + pageSize - 1)
      ) as { data: WeeklyPlanRow[] | null; error: { message?: string } | null }

      if (error) {
        throw new Error(error.message || 'Не удалось загрузить недельный план-факт')
      }

      const batch = data || []
      rows.push(...batch)
      if (batch.length < pageSize) break
      pageFrom += batch.length
    }
  }

  return rows
}

async function fetchPeriodWeeklyPlansChunked(
  supabase: ReturnType<typeof getSupabase>,
  studioIds: number[],
  periodId: number
): Promise<WeeklyPlanRow[]> {
  if (studioIds.length === 0) return []

  const chunkSize = 150
  const pageSize = 1000
  const rows: WeeklyPlanRow[] = []

  for (let index = 0; index < studioIds.length; index += chunkSize) {
    const chunk = studioIds.slice(index, index + chunkSize)
    let pageFrom = 0

    while (true) {
      const { data, error } = await withAuthRecovery(() =>
        supabase
          .from('studio_weekly_plan_vs_fact')
          .select(`
            studio_internal_id,
            period_id,
            partner_number,
            week_id,
            week_number_in_period,
            week_start,
            week_end,
            weekly_gross_revenue_plan,
            weekly_gross_revenue_fact,
            weekly_new_subscriptions_count_plan,
            weekly_new_subscriptions_count_fact,
            weekly_subscription_upgrades_count_plan,
            weekly_subscription_upgrades_count_fact,
            weekly_subscription_returning_clients_count_plan,
            weekly_subscription_returning_clients_count_fact
          `)
          .eq('period_id', periodId)
          .in('studio_internal_id', chunk)
          .order('week_number_in_period', { ascending: true })
          .order('studio_internal_id', { ascending: true })
          .range(pageFrom, pageFrom + pageSize - 1)
      ) as { data: WeeklyPlanRow[] | null; error: { message?: string } | null }

      if (error) {
        throw new Error(error.message || 'Не удалось загрузить план-факт по подпискам за период')
      }

      const batch = data || []
      rows.push(...batch)
      if (batch.length < pageSize) break
      pageFrom += batch.length
    }
  }

  return rows
}

function completion(plan: number, fact: number): number {
  if (!plan) return 0
  return (fact / plan) * 100
}

function formatPercentValue(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    maximumFractionDigits: 1
  })}%`
}

function formatOptionalPercentValue(value: number | null): string {
  return value === null ? '—' : formatPercentValue(value)
}

function formatSignedCount(value: number | null): string {
  if (value === null) return '—'
  if (value > 0) return `+${formatNumber(value)}`
  return formatNumber(value)
}

function getSubscriptionLevel(total: number): SubscriptionLevel {
  let level = SUBSCRIPTION_LEVELS[0]
  for (const candidate of SUBSCRIPTION_LEVELS) {
    if (total >= candidate.threshold) {
      level = candidate
    }
  }
  return level
}

function getNewSubscriptionConversionFromDailyRows(rows: DailyTotalsRow[]) {
  const newVisitors = rows.reduce((sum, row) => sum + (row.new_visitors_count || 0), 0)
  const newSubscriptions = rows.reduce((sum, row) => sum + (row.new_subscriptions_count || 0), 0)

  return {
    new_visitors: newVisitors,
    new_subscriptions: newSubscriptions,
    conversion_pct: newVisitors > 0 ? (newSubscriptions / newVisitors) * 100 : null
  }
}

function maxIsoDate(left: string, right: string): string {
  return left >= right ? left : right
}

function getEffectiveSubscriptionStart(
  rangeStart: string,
  firstSubscriptionSaleDate: string | null
): string {
  if (!firstSubscriptionSaleDate) return rangeStart
  return maxIsoDate(rangeStart, firstSubscriptionSaleDate)
}

function filterRowsFromDate(rows: DailyTotalsRow[], startDate: string): DailyTotalsRow[] {
  return rows.filter((row) => row.metric_date >= startDate)
}

function emptySubscriptionConversion() {
  return {
    new_visitors: 0,
    new_subscriptions: 0,
    conversion_pct: 0
  }
}

function latestNumberByDate(rows: DailyTotalsRow[], key: 'active_subscribers_count'): number | null {
  const sorted = rows
    .filter((row) => row[key] !== null && row[key] !== undefined)
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted[sorted.length - 1]?.[key]
  return value === null || value === undefined ? null : Number(value)
}

function getWeeklySubscriptionPlan(row: WeeklyPlanRow): number {
  return (row.weekly_new_subscriptions_count_plan || 0)
    + (row.weekly_subscription_upgrades_count_plan || 0)
    + (row.weekly_subscription_returning_clients_count_plan || 0)
}

function getWeeklySubscriptionFact(row: WeeklyPlanRow): number {
  return (row.weekly_new_subscriptions_count_fact || 0)
    + (row.weekly_subscription_upgrades_count_fact || 0)
    + (row.weekly_subscription_returning_clients_count_fact || 0)
}

function latestActiveSubscribersOnOrBefore(rows: ActiveSubscribersSnapshotRow[], date: string): number | null {
  const matchingRows = rows
    .filter((row) => row.metric_date <= date && row.active_subscribers_count !== null && row.active_subscribers_count !== undefined)
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = matchingRows[matchingRows.length - 1]?.active_subscribers_count
  return value === null || value === undefined ? null : Number(value)
}

function activeSubscribersOnDate(rows: ActiveSubscribersSnapshotRow[], date: string): number | null {
  const value = rows.find((row) => row.metric_date === date)?.active_subscribers_count
  return value === null || value === undefined ? null : Number(value)
}

function firstActiveSubscribersOnOrAfter(rows: ActiveSubscribersSnapshotRow[], startDate: string, endDate: string): number | null {
  const matchingRows = rows
    .filter((row) => row.metric_date >= startDate && row.metric_date <= endDate && row.active_subscribers_count !== null && row.active_subscribers_count !== undefined)
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = matchingRows[0]?.active_subscribers_count
  return value === null || value === undefined ? null : Number(value)
}

function firstNumberByDate(rows: DailyTotalsRow[], key: 'active_subscribers_count'): number | null {
  const sorted = rows
    .filter((row) => row[key] !== null && row[key] !== undefined)
    .sort((a, b) => a.metric_date.localeCompare(b.metric_date))

  const value = sorted[0]?.[key]
  return value === null || value === undefined ? null : Number(value)
}

const DEFAULT_VISIBLE_ROWS = 10

export function ManagementRatingsPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { studios, partners, isManagementCompany } = useStudioStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [studioSubscriptionRows, setStudioSubscriptionRows] = useState<StudioSubscriptionRow[]>([])
  const [yesterday, setYesterday] = useState<string | null>(null)
  const [weekLabel, setWeekLabel] = useState<string>('')
  const [availablePeriods, setAvailablePeriods] = useState<MetricPeriod[]>([])
  const [metricWeeks, setMetricWeeks] = useState<MetricWeek[]>([])
  const [currentPeriod, setCurrentPeriod] = useState<MetricPeriod | null>(null)
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0)
  const [selectedRangeKey, setSelectedRangeKey] = useState<PeriodRangeKey>('period')
  const [ratingsPeriodId, setRatingsPeriodId] = useState<number | null>(null)
  useEffect(() => {
    if (!isManagementCompany()) {
      navigate('/')
      return
    }
  }, [isManagementCompany, navigate])

  const partnerLabelMap = useMemo(() => {
    return new Map(partners.map((partner) => [partner.partner_number, partner.label]))
  }, [partners])

  useEffect(() => {
    const loadPeriods = async () => {
      if (!user?.id || !isManagementCompany()) return

      const supabase = getSupabase()
      const today = getLocalIsoDate()
      const { data, error } = await withAuthRecovery(() =>
        supabase
          .from('metric_periods')
          .select('*')
          .lte('period_start', today)
          .order('period_start', { ascending: true })
      ) as { data: MetricPeriod[] | null; error: { message?: string } | null }

      if (error || !data || data.length === 0) {
        setAvailablePeriods([])
        setCurrentPeriod(null)
        return
      }

      const currentIndex = data.findIndex((period) => period.period_start <= today && period.period_end >= today)
      const selectedIndex = currentIndex >= 0 ? currentIndex : data.length - 1

      setAvailablePeriods(data)
      setCurrentPeriodIndex(selectedIndex)
      setCurrentPeriod(data[selectedIndex])
    }

    loadPeriods()
  }, [user?.id, isManagementCompany])

  useEffect(() => {
    let isActive = true

    const loadRatings = async () => {
      if (!user?.id || !isManagementCompany() || !currentPeriod) {
        if (isActive) setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        setRatingsPeriodId(null)
        setStudioSubscriptionRows([])

        const supabase = getSupabase()
        const today = getLocalIsoDate()
        const rawYesterday = addDaysToIsoDate(today, -1)
        const yesterdayDate = rawYesterday < currentPeriod.period_start
          ? currentPeriod.period_start
          : (currentPeriod.period_end < rawYesterday ? currentPeriod.period_end : rawYesterday)
        if (!isActive) return
        setYesterday(yesterdayDate)

        const periodData = currentPeriod
        const managementStudios = studios.filter(
          (studio) =>
            (studio.access_kind === 'management_company' || studio.role === 'management_company')
            && studio.status_code === 'OPEN'
        )

        if (managementStudios.length === 0) {
            setStudioSubscriptionRows([])
          setRatingsPeriodId(periodData.period_id)
          setLoading(false)
          return
        }

        const { data: weekData } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .eq('period_id', periodData.period_id)
            .lte('week_start', yesterdayDate)
            .gte('week_end', yesterdayDate)
            .maybeSingle()
        ) as { data: MetricWeek | null; error: { message?: string } | null }

        const currentMetricWeek = weekData || computeWeekFromPeriod(periodData, yesterdayDate)
        if (!currentMetricWeek) {
          throw new Error('Не удалось определить неделю для рейтинга')
        }

        if (!isActive) return
        setWeekLabel(formatDisplayRange(currentMetricWeek.week_start, yesterdayDate))

        const studioIds = managementStudios.map((studio) => studio.studio_internal_id)
        const { data: subscriptionSettings, error: subscriptionError } = await withAuthRecovery(() =>
          supabase
            .from('studio_subscription_settings')
            .select('studio_internal_id, subscription_type, first_subscription_sale_date')
            .in('studio_internal_id', studioIds)
        ) as { data: SubscriptionSettingsRow[] | null; error: { message?: string } | null }

        if (subscriptionError) {
          throw new Error(subscriptionError.message || 'Не удалось загрузить типы подписок')
        }

        const typeByStudioId = new Map<number, StudioSubscriptionType>(
          (subscriptionSettings || []).map((row) => [row.studio_internal_id, row.subscription_type])
        )
        const firstSubscriptionSaleDateByStudioId = new Map<number, string | null>(
          (subscriptionSettings || []).map((row) => [row.studio_internal_id, row.first_subscription_sale_date])
        )
        const subscriptionStudioIds = new Set(
          managementStudios
            .filter((studio) => {
              const type = typeByStudioId.get(studio.studio_internal_id) || 'REGULAR'
              return type === 'SUBSCRIPTION' || type === 'MIXED'
            })
            .map((studio) => studio.studio_internal_id)
        )

        // Load metric_weeks for the period so we can map daily rows to the same week boundaries
        const { data: metricWeeksData } = await withAuthRecovery(() =>
          supabase
            .from('metric_weeks')
            .select('*')
            .eq('period_id', periodData.period_id)
            .order('week_number_in_period', { ascending: true })
        ) as { data: MetricWeek[] | null; error: { message?: string } | null }

        const metricWeeks: MetricWeek[] = metricWeeksData || []
        if (!isActive) return
        setMetricWeeks(metricWeeks)

        const studioCityIds = Array.from(new Set(managementStudios.map((studio) => studio.city_id).filter((cityId) => Number.isFinite(cityId) && cityId > 0)))

        const [
          periodWeeklyPlanRows,
          periodRows,
          activeSubscriberSnapshotsByStudio,
          partnerCoverage,
          citiesData,
          ownershipsData
        ] = await Promise.all([
          fetchPeriodWeeklyPlansChunked(supabase, studioIds, periodData.period_id),
          fetchDailyTotalsChunked(supabase, {
            studioIds,
            startDate: periodData.period_start,
            endDate: yesterdayDate
          }),
          fetchActiveSubscriberSnapshotsChunked(
            supabase,
            Array.from(subscriptionStudioIds),
            addDaysToIsoDate(periodData.period_start, -120),
            addDaysToIsoDate(yesterdayDate, 1)
          ),
          listPartnerCoverage(),
          studioCityIds.length === 0
            ? Promise.resolve({ data: [], error: null })
            : withAuthRecovery(() =>
                supabase
                  .from('cities')
                  .select('city_id, city_name, country, city_land_group, city_population_group')
                  .in('city_id', studioCityIds)
              ) as Promise<{ data: Array<{ city_id: number; city_name: string | null; country: string | null; city_land_group: number | null; city_population_group: number | null }> | null; error: { message?: string } | null }>,
          studioIds.length === 0
            ? Promise.resolve({ data: [], error: null })
            : withAuthRecovery(() =>
                supabase
                  .from('studio_ownerships')
                  .select(`
                    ownership_id,
                    studio_internal_id,
                    start_date,
                    end_date,
                    updated_at,
                    contract_series!inner (
                      partner_number
                    )
                  `)
                  .in('studio_internal_id', studioIds)
              ) as Promise<{ data: Array<{
                ownership_id: number
                studio_internal_id: number
                start_date: string | null
                end_date: string | null
                updated_at: string | null
                contract_series: { partner_number: number | null } | Array<{ partner_number: number | null }>
              }> | null; error: { message?: string } | null }>
        ])

        if (citiesData.error) {
          throw new Error(citiesData.error.message || 'Не удалось загрузить географию городов')
        }
        if (ownershipsData.error) {
          throw new Error(ownershipsData.error.message || 'Не удалось загрузить владельцев студий')
        }

        const cityById = new Map<number, {
          city_name: string
          country: string | null
          city_land_group: number | null
          city_population_group: number | null
        }>((citiesData.data || []).map((row) => [
          row.city_id,
          {
            city_name: row.city_name || 'Город не указан',
            country: row.country,
            city_land_group: row.city_land_group,
            city_population_group: row.city_population_group
          }
        ]))

        const ownershipRowsByStudioId = new Map<number, Array<{
          studio_internal_id: number
          start_date: string | null
          end_date: string | null
          partner_number: number | null
          rank: number
        }>>()
        const ownershipPartnerByStudioId = new Map<number, number | null>()
        const ownershipRankByStudioId = new Map<number, number>()
        for (const row of ownershipsData.data || []) {
          const startDate = row.start_date ? String(row.start_date).slice(0, 10) : null
          const endDate = row.end_date ? String(row.end_date).slice(0, 10) : null

          const contractSeries = Array.isArray(row.contract_series)
            ? row.contract_series[0]
            : row.contract_series
          const partnerNumber = contractSeries?.partner_number ?? null
          const rankingMoment = row.updated_at || row.start_date || ''
          const ownershipRank = new Date(rankingMoment || '1970-01-01T00:00:00Z').getTime() * 1000 + row.ownership_id

          const list = ownershipRowsByStudioId.get(row.studio_internal_id) || []
          list.push({
            studio_internal_id: row.studio_internal_id,
            start_date: startDate,
            end_date: endDate,
            partner_number: partnerNumber == null ? null : Number(partnerNumber),
            rank: ownershipRank
          })
          ownershipRowsByStudioId.set(row.studio_internal_id, list)

          if ((startDate && startDate > yesterdayDate) || (endDate && endDate <= yesterdayDate)) {
            continue
          }

          const currentRank = ownershipRankByStudioId.get(row.studio_internal_id) ?? Number.NEGATIVE_INFINITY
          if (ownershipRank >= currentRank) {
            ownershipRankByStudioId.set(row.studio_internal_id, ownershipRank)
            ownershipPartnerByStudioId.set(row.studio_internal_id, partnerNumber == null ? null : Number(partnerNumber))
          }
        }

        for (const entries of ownershipRowsByStudioId.values()) {
          entries.sort((a, b) => b.rank - a.rank)
        }

        const resolvePartnerForStudioOnDate = (studioId: number, metricDate: string): number | null => {
          const ownershipRows = ownershipRowsByStudioId.get(studioId) || []
          for (const row of ownershipRows) {
            if (row.start_date && row.start_date > metricDate) continue
            if (row.end_date && row.end_date <= metricDate) continue
            return row.partner_number
          }
          return studioMetaAll.get(studioId)?.partner_number ?? null
        }

        const studioMetaAll = new Map(
          managementStudios.map((studio) => {
            const city = cityById.get(studio.city_id)
            const resolvedPartnerNumber = ownershipPartnerByStudioId.has(studio.studio_internal_id)
              ? ownershipPartnerByStudioId.get(studio.studio_internal_id) ?? null
              : (studio.partner_number ?? null)
            return [
              studio.studio_internal_id,
              {
                studio_number: studio.studio_number,
                city_name: city?.city_name || studio.city?.city_name || 'Город не указан',
                country: city?.country || studio.city?.country || null,
                partner_number: resolvedPartnerNumber,
                partner_label: resolvedPartnerNumber != null
                  ? (partnerLabelMap.get(resolvedPartnerNumber) || `Франчайзи ${resolvedPartnerNumber}`).replace(/ \(№\s*\d+\)/g, '')
                  : 'Франчайзи не указан',
                address: studio.address || 'Адрес не указан',
                city_land_group: city?.city_land_group ?? studio.city?.city_land_group ?? null,
                city_population_group: city?.city_population_group ?? studio.city?.city_population_group ?? null
              }
            ]
          })
        ) as Map<number, StudioMetaData>

        const eligibleManagementStudioIds = new Set<number>()
        for (const [studioId, meta] of studioMetaAll.entries()) {
          if (meta.partner_number !== MANAGEMENT_BRANCH_PARTNER_NUMBER) {
            eligibleManagementStudioIds.add(studioId)
          }
        }

        const studioMeta = new Map(
          Array.from(studioMetaAll.entries()).filter(([studioId]) => eligibleManagementStudioIds.has(studioId))
        ) as Map<number, StudioMetaData>

        const coverageByPartner = new Map<number, PartnerCoverageRow>(
          partnerCoverage.map((row) => [Number(row.partner_number), row])
        )

        const getPartnerLabel = (partnerNumber: number | null | undefined): string => {
          if (partnerNumber == null) return 'Франчайзи не указан'
          const coverage = coverageByPartner.get(Number(partnerNumber))
          const parts = [coverage?.persons_fio, coverage?.cities_text].filter(Boolean)
          if (parts.length > 0) return `${parts.join(', ')} (${partnerNumber})`
          return partnerLabelMap.get(partnerNumber) || `Франчайзи ${partnerNumber}`
        }

        const getPartnerShortLabel = (partnerNumber: number | null | undefined): string => {
          if (partnerNumber == null) return 'Франчайзи не указан'
          const coverage = coverageByPartner.get(Number(partnerNumber))
          if (coverage?.persons_fio) return `${coverage.persons_fio} (${partnerNumber})`
          return partnerLabelMap.get(partnerNumber) || `Франчайзи ${partnerNumber}`
        }

        const weekLabels = Array.from(
          new Map(
            periodWeeklyPlanRows
              .filter((row) => subscriptionStudioIds.has(row.studio_internal_id) && eligibleManagementStudioIds.has(row.studio_internal_id))
              .sort((a, b) => a.week_number_in_period - b.week_number_in_period)
              .map((row) => [row.week_number_in_period, {
                weekNumber: row.week_number_in_period,
                weekEnd: row.week_end,
                label: `W${row.week_number_in_period}`
              }])
          ).values()
        )
        const periodRowsByStudio = new Map<number, DailyTotalsRow[]>()
        for (const row of periodRows) {
          if (!eligibleManagementStudioIds.has(row.studio_internal_id)) continue
          if (!subscriptionStudioIds.has(row.studio_internal_id)) continue
          const list = periodRowsByStudio.get(row.studio_internal_id) || []
          list.push(row)
          periodRowsByStudio.set(row.studio_internal_id, list)
        }

        const weeklyPlanRowsByStudio = new Map<number, WeeklyPlanRow[]>()
        for (const row of periodWeeklyPlanRows) {
          if (!eligibleManagementStudioIds.has(row.studio_internal_id)) continue
          if (!subscriptionStudioIds.has(row.studio_internal_id)) continue
          const list = weeklyPlanRowsByStudio.get(row.studio_internal_id) || []
          list.push(row)
          weeklyPlanRowsByStudio.set(row.studio_internal_id, list)
        }

        // Группируем дневные данные по неделям для каждой студии
        const dailyRowsByStudioAndWeek = new Map<string, DailyTotalsRow[]>()
        for (const row of periodRows) {
          if (!eligibleManagementStudioIds.has(row.studio_internal_id)) continue
          if (!subscriptionStudioIds.has(row.studio_internal_id)) continue
          // Prefer DB-provided metric_weeks to compute week mapping so it matches weekly plan/fact
          let week: MetricWeek | null = null
          week = metricWeeks.find((mw) => row.metric_date >= mw.week_start && row.metric_date <= mw.week_end) || null
          if (!week) {
            week = computeWeekFromPeriod(periodData, row.metric_date)
          }
          if (!week) continue
          const key = `${row.studio_internal_id}-${week.week_number_in_period}`
          const list = dailyRowsByStudioAndWeek.get(key) || []
          list.push(row)
          dailyRowsByStudioAndWeek.set(key, list)
        }

        const builtStudioSubscriptionRows = Array.from(subscriptionStudioIds)
            .filter((studioId) => eligibleManagementStudioIds.has(studioId))
            .map((studioId) => {
              const rows = periodRowsByStudio.get(studioId) || []
              const planRows = weeklyPlanRowsByStudio.get(studioId) || []
              const activeSnapshots = activeSubscriberSnapshotsByStudio.get(studioId) || []
              const meta = studioMeta.get(studioId)
              const partnerNumber = meta?.partner_number
              const firstSubscriptionSaleDate = firstSubscriptionSaleDateByStudioId.get(studioId) || null
              const hasStartedSubscriptionHistory = Boolean(firstSubscriptionSaleDate)
              const effectivePeriodStart = hasStartedSubscriptionHistory
                ? getEffectiveSubscriptionStart(periodData.period_start, firstSubscriptionSaleDate)
                : periodData.period_start
              const periodRowsFromTransition = hasStartedSubscriptionHistory
                ? filterRowsFromDate(rows, effectivePeriodStart)
                : []
              const subscribersAtStart = hasStartedSubscriptionHistory
                ? firstActiveSubscribersOnOrAfter(activeSnapshots, effectivePeriodStart, yesterdayDate)
                  ?? latestActiveSubscribersOnOrBefore(activeSnapshots, addDaysToIsoDate(effectivePeriodStart, -1))
                  ?? firstNumberByDate(periodRowsFromTransition, 'active_subscribers_count')
                : null
              const subscribersAtEnd = hasStartedSubscriptionHistory
                ? latestActiveSubscribersOnOrBefore(activeSnapshots, yesterdayDate)
                  ?? latestNumberByDate(periodRowsFromTransition, 'active_subscribers_count')
                : null
              const activeSubscribersForPeriod = subscribersAtEnd ?? 0
              const newSubscriptionSales = hasStartedSubscriptionHistory
                ? planRows.reduce((sum, row) => sum + (row.weekly_new_subscriptions_count_fact || 0), 0)
                : 0
              const subscriptionUpgrades = hasStartedSubscriptionHistory
                ? planRows.reduce((sum, row) => sum + (row.weekly_subscription_upgrades_count_fact || 0), 0)
                : 0
              const returningSubscriptionClients = hasStartedSubscriptionHistory
                ? planRows.reduce((sum, row) => sum + (row.weekly_subscription_returning_clients_count_fact || 0), 0)
                : 0
              const newConversion = hasStartedSubscriptionHistory
                ? getNewSubscriptionConversionFromDailyRows(periodRowsFromTransition)
                : emptySubscriptionConversion()
              const periodPlan = planRows.reduce((sum, row) => sum + getWeeklySubscriptionPlan(row), 0)
              const periodFact = newSubscriptionSales + subscriptionUpgrades + returningSubscriptionClients
              const periodPlanNew = planRows.reduce((sum, row) => sum + (row.weekly_new_subscriptions_count_plan || 0), 0)
              const periodFactNew = newSubscriptionSales
              const churnCount = subscribersAtStart !== null && subscribersAtEnd !== null
                ? Math.max(0, subscribersAtStart + newSubscriptionSales + subscriptionUpgrades + returningSubscriptionClients - subscribersAtEnd)
                : null
              const retentionPct = subscribersAtStart && subscribersAtStart > 0 && subscribersAtEnd !== null
                ? ((subscribersAtEnd - newSubscriptionSales - subscriptionUpgrades - returningSubscriptionClients) / subscribersAtStart) * 100
                : null
              const churnPct = churnCount === null
                ? null
                : subscribersAtStart && subscribersAtStart > 0
                  ? (churnCount / subscribersAtStart) * 100
                  : null

              return {
                studio_internal_id: studioId,
                studio_number: meta?.studio_number || String(studioId),
                city_name: meta?.city_name || 'Город не указан',
                address: meta?.address || 'Адрес не указан',
                partner_number: partnerNumber == null ? 0 : Number(partnerNumber),
                partner_label: partnerNumber == null ? 'Франчайзи не указан' : getPartnerLabel(Number(partnerNumber)),
                partner_short_label: partnerNumber == null ? 'Франчайзи не указан' : getPartnerShortLabel(Number(partnerNumber)),
                period_plan: periodPlan,
                period_fact: periodFact,
                period_plan_new: periodPlanNew,
                period_fact_new: periodFactNew,
                period_fact_upgrades: subscriptionUpgrades,
                period_fact_returning: returningSubscriptionClients,
                completion_pct: completion(periodPlan, periodFact),
                active_subscribers: activeSubscribersForPeriod,
                active_subscribers_growth: subscribersAtStart !== null
                  ? activeSubscribersForPeriod - subscribersAtStart
                  : null,
                // For period view: prefer weekly-plan-fact aggregation for new subscriptions (more reliable)
                new_visitors: periodRowsFromTransition.reduce((sum, r) => sum + (r.new_visitors_count || 0), 0),
                new_subscriptions: newConversion.new_subscriptions,
                conversion_pct: newConversion.conversion_pct,
                subscribers_at_start: subscribersAtStart,
                subscribers_at_end: subscribersAtEnd,
                churn_count: churnCount,
                churn_pct: churnPct,
                retention_pct: retentionPct,
                weeks: weekLabels.map((week) => {
                  const weekRows = planRows.filter((row) => row.week_number_in_period === week.weekNumber)
                  const weekDailyKey = `${studioId}-${week.weekNumber}`
                  const weekDailyRows = dailyRowsByStudioAndWeek.get(weekDailyKey) || []
                  const rawWeekEnd = week.weekEnd
                  const weekEnd = rawWeekEnd > yesterdayDate ? yesterdayDate : rawWeekEnd
                  const possibleWeekStart = addDaysToIsoDate(week.weekEnd, -6)
                  const weekStart = possibleWeekStart < periodData.period_start ? periodData.period_start : possibleWeekStart
                  const effectiveWeekStart = hasStartedSubscriptionHistory
                    ? getEffectiveSubscriptionStart(weekStart, firstSubscriptionSaleDate)
                    : weekStart
                  const weekRowsFromTransition = hasStartedSubscriptionHistory
                    ? filterRowsFromDate(weekDailyRows, effectiveWeekStart)
                    : []

                  const weekNewConversion = hasStartedSubscriptionHistory
                    ? getNewSubscriptionConversionFromDailyRows(weekRowsFromTransition)
                    : emptySubscriptionConversion()
                  const weekPlanNewSum = weekRows.reduce((sum, row) => sum + (row.weekly_new_subscriptions_count_plan || 0), 0)
                  const weekFactNewSum = hasStartedSubscriptionHistory
                    ? weekRows.reduce((sum, row) => sum + (row.weekly_new_subscriptions_count_fact || 0), 0)
                    : 0
                  const weekUpgrades = hasStartedSubscriptionHistory
                    ? weekRows.reduce((sum, row) => sum + (row.weekly_subscription_upgrades_count_fact || 0), 0)
                    : 0
                  const weekReturning = hasStartedSubscriptionHistory
                    ? weekRows.reduce((sum, row) => sum + (row.weekly_subscription_returning_clients_count_fact || 0), 0)
                    : 0
                  const weekNewSubscriptions = weekFactNewSum

                  const weekSubscribersAtStart = hasStartedSubscriptionHistory
                    ? firstActiveSubscribersOnOrAfter(activeSnapshots, effectiveWeekStart, weekEnd)
                      ?? latestActiveSubscribersOnOrBefore(activeSnapshots, addDaysToIsoDate(effectiveWeekStart, -1))
                      ?? firstNumberByDate(weekRowsFromTransition, 'active_subscribers_count')
                    : null
                  const weekSubscribersAtEnd = hasStartedSubscriptionHistory
                    ? latestActiveSubscribersOnOrBefore(activeSnapshots, weekEnd)
                      ?? latestNumberByDate(weekRowsFromTransition, 'active_subscribers_count')
                    : null

                  const weekChurnCount = weekSubscribersAtStart !== null && weekSubscribersAtEnd !== null
                    ? Math.max(0, (weekSubscribersAtStart || 0) + weekNewSubscriptions + weekUpgrades + weekReturning - (weekSubscribersAtEnd || 0))
                    : null
                  const weekRetentionPct = weekSubscribersAtStart && weekSubscribersAtStart > 0 && weekSubscribersAtEnd !== null
                    ? ((weekSubscribersAtEnd as number) - weekNewSubscriptions - weekUpgrades - weekReturning) / (weekSubscribersAtStart as number) * 100
                    : null
                  const weekChurnPct = weekChurnCount === null
                    ? null
                    : weekSubscribersAtStart && weekSubscribersAtStart > 0
                      ? ((weekChurnCount || 0) / (weekSubscribersAtStart || 0)) * 100
                      : null

                  return {
                    label: week.label,
                    week_end: week.weekEnd,
                    plan: weekRows.reduce((sum, row) => sum + getWeeklySubscriptionPlan(row), 0),
                    fact: weekNewSubscriptions + weekUpgrades + weekReturning,
                    plan_new: weekPlanNewSum,
                    fact_new: weekFactNewSum,
                    fact_upgrades: weekUpgrades,
                    fact_returning: weekReturning,
                    active_subscribers: hasStartedSubscriptionHistory
                      ? latestActiveSubscribersOnOrBefore(activeSnapshots, weekEnd) ?? 0
                      : 0,
                    active_subscribers_growth: weekSubscribersAtStart !== null
                      ? (latestActiveSubscribersOnOrBefore(activeSnapshots, weekEnd) ?? 0) - (weekSubscribersAtStart as number)
                      : null,
                    new_visitors: weekNewConversion.new_visitors,
                    new_subscriptions: weekNewSubscriptions,
                    conversion_pct: (weekNewConversion.new_visitors || 0) > 0 ? (weekNewSubscriptions / (weekNewConversion.new_visitors || 0)) * 100 : null,
                    churn_count: weekChurnCount,
                    retention_pct: weekRetentionPct,
                    churn_pct: weekChurnPct
                  }
                })
              }
              })
              .sort((a, b) => {
              if (b.active_subscribers !== a.active_subscribers) return b.active_subscribers - a.active_subscribers
              if (b.period_fact !== a.period_fact) return b.period_fact - a.period_fact
              return a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
            })
        
          if (!isActive) return
          setStudioSubscriptionRows(builtStudioSubscriptionRows)

        if (!isActive) return
        setRatingsPeriodId(periodData.period_id)
      } catch (err) {
        if (!isActive) return
        console.error('❌ ManagementRatingsPage:', err)
        setError(err instanceof Error ? err.message : 'Не удалось загрузить рейтинги')
      } finally {
        if (isActive) setLoading(false)
      }
    }

    loadRatings()

    return () => {
      isActive = false
    }
  }, [user?.id, studios, partners, isManagementCompany, partnerLabelMap, currentPeriod, selectedRangeKey])

  const pageTitle = 'Рейтинги по подпискам'
  const selectedWeekNumber = selectedRangeKey.startsWith('week-')
    ? Number(selectedRangeKey.replace('week-', ''))
    : null
  const effectivePeriodEnd = currentPeriod
    ? (
        yesterday && yesterday < currentPeriod.period_end
          ? yesterday
          : currentPeriod.period_end
      )
    : null
  const selectedMetricWeek = selectedWeekNumber
    ? metricWeeks.find((week) => week.week_number_in_period === selectedWeekNumber) || null
    : null
  const getVisibleActiveSubscribers = (row: Pick<StudioSubscriptionRow, 'active_subscribers' | 'weeks'>) => {
    if (!selectedWeekNumber) return row.active_subscribers

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return week?.active_subscribers ?? 0
  }
  const getVisibleActiveSubscribersGrowth = (row: Pick<StudioSubscriptionRow, 'active_subscribers_growth' | 'weeks'>) => {
    if (!selectedWeekNumber) return row.active_subscribers_growth

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return week?.active_subscribers_growth ?? null
  }
  const subscriptionRankingRows = studioSubscriptionRows
    .map((row) => ({
      ...row,
      active_subscribers: getVisibleActiveSubscribers(row),
      active_subscribers_growth: getVisibleActiveSubscribersGrowth(row),
      subscription_level: getSubscriptionLevel(getVisibleActiveSubscribers(row))
    }))
    .sort((a, b) => {
      if (b.active_subscribers !== a.active_subscribers) return b.active_subscribers - a.active_subscribers
      if (b.period_fact !== a.period_fact) return b.period_fact - a.period_fact
      const cityCompare = a.city_name.localeCompare(b.city_name, 'ru', { numeric: true })
      return cityCompare !== 0
        ? cityCompare
        : a.studio_number.localeCompare(b.studio_number, 'ru', { numeric: true })
    })
    .map((row, index) => ({ ...row, place: index + 1 }))
  const rangeOptions = (() => {
    const visibleWeeks = metricWeeks
      .filter((week) => !effectivePeriodEnd || week.week_start <= effectivePeriodEnd)
      .sort((a, b) => a.week_number_in_period - b.week_number_in_period)
    return [
      { value: 'period' as PeriodRangeKey, label: 'Весь период' },
      ...visibleWeeks.map((week) => ({
        value: `week-${week.week_number_in_period}` as PeriodRangeKey,
        label: `Неделя ${week.week_number_in_period}`
      }))
    ]
  })()
  const subscriptionPlanFactLabel = selectedWeekNumber ? `Продажи подписок: неделя ${selectedWeekNumber}` : 'Продажи подписок: весь период'
  const selectedRange = (() => {
    if (!currentPeriod || !effectivePeriodEnd) return null
    if (!selectedMetricWeek) {
      return {
        mode: 'period' as const,
        start: currentPeriod.period_start,
        end: effectivePeriodEnd,
        label: 'Весь период'
      }
    }

    return {
      mode: 'week' as const,
      start: selectedMetricWeek.week_start,
      end: selectedMetricWeek.week_end < effectivePeriodEnd ? selectedMetricWeek.week_end : effectivePeriodEnd,
      label: `Неделя ${selectedMetricWeek.week_number_in_period}`
    }
  })()
  const effectiveRangeLabel = selectedRange
    ? formatDisplayRange(selectedRange.start, selectedRange.end)
    : 'Диапазон не определен'
  const getVisibleSubscriptionPlanFact = (
    row: Pick<
      StudioSubscriptionRow,
      'period_plan' | 'period_fact' | 'period_fact_new' | 'period_fact_upgrades' | 'period_fact_returning' | 'weeks'
    >
  ) => {
    if (!selectedWeekNumber) {
      return {
        plan: row.period_plan,
        fact: row.period_fact,
        fact_new: row.period_fact_new,
        fact_upgrades: row.period_fact_upgrades,
        fact_returning: row.period_fact_returning
      }
    }

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return {
      plan: week?.plan || 0,
      fact: week?.fact || 0,
      fact_new: week?.fact_new || 0,
      fact_upgrades: week?.fact_upgrades || 0,
      fact_returning: week?.fact_returning || 0
    }
  }

  const getVisibleSubscriptionMetrics = (row: Pick<StudioSubscriptionRow, 'new_visitors' | 'new_subscriptions' | 'conversion_pct' | 'weeks'>) => {
    if (!selectedWeekNumber) {
      return {
        new_visitors: row.new_visitors,
        new_subscriptions: row.new_subscriptions,
        conversion_pct: row.conversion_pct
      }
    }

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return {
      new_visitors: week?.new_visitors ?? 0,
      new_subscriptions: week?.new_subscriptions ?? 0,
      conversion_pct: week?.conversion_pct ?? null
    }
  }

  const getVisibleSubscriptionRetention = (row: Pick<StudioSubscriptionRow, 'retention_pct' | 'weeks'>) => {
    if (!selectedWeekNumber) return row.retention_pct

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return week?.retention_pct ?? null
  }

  const getVisibleSubscriptionChurn = (row: Pick<StudioSubscriptionRow, 'churn_pct' | 'weeks'>) => {
    if (!selectedWeekNumber) return row.churn_pct

    const week = row.weeks.find((item) => item.label === `W${selectedWeekNumber}`)
    return week?.churn_pct ?? null
  }

  const handlePeriodChange = (direction: 'prev' | 'next') => {
    const nextIndex = direction === 'prev' ? currentPeriodIndex - 1 : currentPeriodIndex + 1
    if (nextIndex < 0 || nextIndex >= availablePeriods.length) return
    setCurrentPeriodIndex(nextIndex)
    setCurrentPeriod(availablePeriods[nextIndex])
    setSelectedRangeKey('period')
  }

  useEffect(() => {
    if (!rangeOptions.some((option) => option.value === selectedRangeKey)) {
      setSelectedRangeKey('period')
    }
  }, [rangeOptions, selectedRangeKey])

  if (loading) {
    return <LoadingScreen />
  }

  if (!error && currentPeriod && ratingsPeriodId !== currentPeriod.period_id) {
    return <LoadingScreen />
  }

  const canGoNextPeriod = (() => {
    if (currentPeriodIndex >= availablePeriods.length - 1) return false
    const nextPeriod = availablePeriods[currentPeriodIndex + 1]
    return nextPeriod ? nextPeriod.period_start <= getLocalIsoDate() : false
  })()

  return (
    <div className="space-y-6 pb-24">
      <Card className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => navigate('/management-ratings')}
              className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Рейтинги сети
            </button>
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              <h1 className="text-xl font-semibold">{pageTitle}</h1>
            </div>
          </div>
          <div className="rounded-xl bg-primary/10 px-3 py-2 text-sm text-primary">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>Неделя по вашему календарю: {weekLabel || 'не определена'}</span>
            </div>
            <div className="mt-1 text-xs text-primary/80">
              {effectiveRangeLabel}
            </div>
          </div>
        </div>
        {yesterday ? (
          <p className="text-sm text-muted-foreground">
            Срез по последней доступной дате: {formatDisplayDate(yesterday)}. Недельный рейтинг считается от начала недели до этой даты включительно.
          </p>
        ) : null}
      </Card>

      {currentPeriod ? (
        <PeriodSelector
          currentPeriod={currentPeriod}
          onPrevPeriod={() => handlePeriodChange('prev')}
          onNextPeriod={() => handlePeriodChange('next')}
          canGoPrev={currentPeriodIndex > 0}
          canGoNext={canGoNextPeriod}
          showViewModeSelector
          isCurrentPeriod={currentPeriod.period_start <= getLocalIsoDate() && currentPeriod.period_end >= getLocalIsoDate()}
          periodFilterValue={selectedRangeKey}
          periodFilterOptions={rangeOptions}
          onPeriodFilterChange={(value) => setSelectedRangeKey(value as PeriodRangeKey)}
          selectedRange={selectedRange}
        />
      ) : null}

      {error ? (
        <Card className="border border-destructive/20 bg-destructive/5">
          <p className="text-sm text-destructive">{error}</p>
        </Card>
      ) : null}

      <Card className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Активные подписки</h2>
              <p className="text-xs text-muted-foreground">{effectiveRangeLabel}</p>
            </div>
            <div className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground">
              показано {Math.min(DEFAULT_VISIBLE_ROWS, subscriptionRankingRows.length)} из {subscriptionRankingRows.length}
            </div>
          </div>

          {subscriptionRankingRows.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border px-4 py-8 text-center text-sm text-muted-foreground">
              Нет данных по активным подпискам за выбранный период.
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-border/70 bg-background/80">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[1280px] text-sm">
                  <thead className="sticky top-0 z-10 border-b border-border bg-muted/95 backdrop-blur">
                    <tr>
                      <th className="w-72 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Франчайзи / студия</th>
                      <th className="w-56 px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Город / адрес</th>
                      <th className="w-32 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Активных подписок</th>
                      <th className="w-32 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Грейд</th>
                      <th className="w-44 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{subscriptionPlanFactLabel}</th>
                      <th className="w-32 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">% плана</th>
                      <th className="w-40 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Новые: продажи / приходы</th>
                      <th className="w-36 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Отток подписок, %</th>
                      <th className="w-36 px-3 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Удержание подписок</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptionRankingRows.map((row, index) => (
                      <tr key={row.studio_internal_id} className={`border-b border-border/60 align-top last:border-b-0 ${partnerRowAccentTone(index + 1)}`}>
                        <td className="px-3 py-3">
                          <div className="font-semibold">№{row.studio_number}</div>
                          <div className="max-w-[260px] break-words text-xs text-muted-foreground">{row.partner_short_label}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-semibold">{row.city_name}</div>
                          <div className="text-xs text-muted-foreground">{row.address}</div>
                        </td>
                        <td className="px-3 py-3 text-right tabular-nums">
                          <div className="font-semibold text-primary">{formatNumber(getVisibleActiveSubscribers(row))}</div>
                          <div className={`text-xs ${
                            (row.active_subscribers_growth ?? 0) > 0
                              ? 'text-success'
                              : (row.active_subscribers_growth ?? 0) < 0
                                ? 'text-error'
                                : 'text-muted-foreground'
                          }`}>
                            {formatSignedCount(row.active_subscribers_growth)}
                          </div>
                        </td>
                        {(() => {
                          const level = getSubscriptionLevel(getVisibleActiveSubscribers(row))
                          return (
                            <td className="px-3 py-3 text-right">
                              <span
                                className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                                style={{ backgroundColor: level.color }}
                              >
                                {level.name}
                              </span>
                              <div className="mt-1 text-xs tabular-nums text-muted-foreground">{level.threshold}+</div>
                            </td>
                          )
                        })()}
                        {(() => {
                          const planFact = getVisibleSubscriptionPlanFact(row)
                          return (
                            <>
                              <td className="px-3 py-3 text-right tabular-nums">
                                {formatNumber(planFact.fact)} / {formatNumber(planFact.plan)}
                              </td>
                              <td className="px-3 py-3 text-right font-semibold tabular-nums">{formatPercentValue(completion(planFact.plan, planFact.fact))}</td>
                            </>
                          )
                        })()}
                        {(() => {
                          const metrics = getVisibleSubscriptionMetrics(row)
                          return (
                            <td className="px-3 py-3 text-right tabular-nums">
                              <div className="font-semibold">{formatOptionalPercentValue(metrics.conversion_pct)}</div>
                              <div className="text-xs text-muted-foreground">{formatNumber(metrics.new_subscriptions)} / {formatNumber(metrics.new_visitors)}</div>
                            </td>
                          )
                        })()}
                        <td className="px-3 py-3 text-right tabular-nums">
                          {formatOptionalPercentValue(getVisibleSubscriptionChurn(row))}
                        </td>
                        <td className="px-3 py-3 text-right font-semibold tabular-nums">
                          {formatOptionalPercentValue(getVisibleSubscriptionRetention(row))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Card>

      <ManagementCompanyNavigation />
    </div>
  )
}
